import React, { Children } from 'react';
import { motion } from 'framer-motion';
import { ShieldCheckIcon, LockKeyholeIcon, BadgeCheckIcon, UsersIcon } from 'lucide-react';
const trustSignals = [{
  icon: LockKeyholeIcon,
  title: 'Powered by Stripe',
  description: 'The same infrastructure trusted by Amazon, Google, and Shopify.'
}, {
  icon: ShieldCheckIcon,
  title: 'Bank-grade encryption',
  description: '256-bit encryption on every transaction. Your data is always protected.'
}, {
  icon: UsersIcon,
  title: 'Your money, your control',
  description: 'Funds stay within your group. Withdraw anytime.'
}, {
  icon: BadgeCheckIcon,
  title: 'Regulated payments',
  description: 'Processed by Stripe — a globally regulated provider. Your funds are safe.'
}];
const containerVariants = {
  hidden: {},
  visible: {
    transition: {
      staggerChildren: 0.1
    }
  }
};
const itemVariants = {
  hidden: {
    opacity: 0,
    y: 16
  },
  visible: {
    opacity: 1,
    y: 0,
    transition: {
      duration: 0.45,
      ease: 'easeOut'
    }
  }
};
export function TrustSecurity() {
  return <section className="py-24 lg:py-32 bg-gray-950 text-white relative overflow-hidden" data-id="element-248">
      {/* Subtle glow */}
      <div className="absolute inset-0 pointer-events-none" data-id="element-249">
        <div className="absolute bottom-0 right-1/4 w-[600px] h-[400px] bg-kitty-yellow/[0.04] rounded-full blur-3xl" data-id="element-250" />
      </div>

      <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-251">
        <motion.div initial={{
        opacity: 0,
        y: 20
      }} whileInView={{
        opacity: 1,
        y: 0
      }} viewport={{
        once: true,
        margin: '-80px'
      }} transition={{
        duration: 0.5
      }} className="text-center max-w-2xl mx-auto mb-16 lg:mb-20" data-id="element-252">
          <div className="inline-flex items-center gap-2 bg-white/[0.06] border border-white/[0.1] rounded-full px-4 py-1.5 mb-6" data-id="element-253">
            <ShieldCheckIcon className="w-4 h-4 text-kitty-yellow" data-id="element-254" />
            <span className="text-sm font-medium text-gray-300" data-id="element-255">
              Security & Trust
            </span>
          </div>
          <h2 className="text-3xl md:text-5xl font-extrabold leading-tight tracking-tight mb-5" data-id="element-256">
            Your money is safe.{' '}
            <span className="text-kitty-yellow" data-id="element-257">Always.</span>
          </h2>
          <p className="text-lg text-gray-400 max-w-md mx-auto leading-relaxed" data-id="element-258">
            Built on the same infrastructure banks trust.
          </p>
        </motion.div>

        <motion.div variants={containerVariants} initial="hidden" whileInView="visible" viewport={{
        once: true,
        margin: '-60px'
      }} className="grid sm:grid-cols-2 lg:grid-cols-4 gap-5 lg:gap-6" data-id="element-259">
          {trustSignals.map(item => <motion.div key={item.title} variants={itemVariants} className="rounded-2xl bg-white/[0.04] border border-white/[0.08] p-6 lg:p-8 text-center flex flex-col items-center" data-id="element-260">
              <div className="w-12 h-12 rounded-2xl bg-kitty-yellow/10 flex items-center justify-center mb-5" data-id="element-261">
                <item.icon className="w-6 h-6 text-kitty-yellow" data-id="element-262" />
              </div>
              <h3 className="text-lg font-bold text-white mb-2" data-id="element-263">
                {item.title}
              </h3>
              <p className="text-sm text-gray-500 leading-relaxed" data-id="element-264">
                {item.description}
              </p>
            </motion.div>)}
        </motion.div>

        {/* Trust footer line */}
        <motion.div initial={{
        opacity: 0
      }} whileInView={{
        opacity: 1
      }} viewport={{
        once: true
      }} transition={{
        duration: 0.6,
        delay: 0.4
      }} className="flex items-center justify-center gap-6 mt-14 lg:mt-18" data-id="element-265">
          <div className="flex items-center gap-2 text-gray-600" data-id="element-266">
            <LockKeyholeIcon className="w-4 h-4" data-id="element-267" />
            <span className="text-xs font-medium uppercase tracking-wider" data-id="element-268">
              256-bit SSL
            </span>
          </div>
          <div className="w-px h-4 bg-gray-800" data-id="element-269" />
          <div className="flex items-center gap-2 text-gray-600" data-id="element-270">
            <ShieldCheckIcon className="w-4 h-4" data-id="element-271" />
            <span className="text-xs font-medium uppercase tracking-wider" data-id="element-272">
              PCI Compliant
            </span>
          </div>
          <div className="w-px h-4 bg-gray-800" data-id="element-273" />
          <div className="flex items-center gap-2 text-gray-600" data-id="element-274">
            <BadgeCheckIcon className="w-4 h-4" data-id="element-275" />
            <span className="text-xs font-medium uppercase tracking-wider" data-id="element-276">
              Stripe Secured
            </span>
          </div>
        </motion.div>
      </div>
    </section>;
}